// Big-endian binary writer for PSD encoding.
//
// Counterpart to `BinaryReader`.  PSD is big-endian throughout, so every
// multi-byte write here is BE.  The class owns a growable Uint8Array and a
// cursor; reads/writes/seeks update the cursor.  Out-of-bounds writes do not
// happen — the buffer simply grows on demand.
//
// Deferred-length pattern: sections in PSD are length-prefixed with the
// length field appearing *before* the content.  We support that with the
// `reserveU32` / `reserveU64` helpers: they consume the slot, hand back a
// `commit(totalContentBytes)` callback, and you call that once you know how
// many bytes you wrote between the reservation and now.  See `writePsd.ts`
// for the canonical pattern.

export class BinaryWriterError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BinaryWriterError";
  }
}

const INITIAL_CAPACITY = 4096;

export class BinaryWriter {
  private buf: Uint8Array;
  private view: DataView;
  private offset: number;

  constructor(initialCapacity: number = INITIAL_CAPACITY) {
    const cap = Math.max(16, initialCapacity | 0);
    this.buf = new Uint8Array(cap);
    this.view = new DataView(this.buf.buffer);
    this.offset = 0;
  }

  // -- Cursor / size ----------------------------------------------------

  length(): number {
    return this.offset;
  }

  toUint8Array(): Uint8Array {
    return this.buf.slice(0, this.offset);
  }

  private ensure(n: number): void {
    if (this.offset + n <= this.buf.byteLength) return;
    let cap = this.buf.byteLength;
    while (cap < this.offset + n) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf);
    this.buf = next;
    this.view = new DataView(this.buf.buffer);
  }

  // -- Integers (big-endian) -------------------------------------------

  writeU8(v: number): void {
    this.ensure(1);
    this.view.setUint8(this.offset, v & 0xff);
    this.offset += 1;
  }

  writeU16(v: number): void {
    this.ensure(2);
    this.view.setUint16(this.offset, v & 0xffff, false);
    this.offset += 2;
  }

  writeI16(v: number): void {
    this.ensure(2);
    this.view.setInt16(this.offset, v, false);
    this.offset += 2;
  }

  writeU32(v: number): void {
    this.ensure(4);
    // Use unsigned coercion: ">>> 0" keeps the bits but loses sign.
    this.view.setUint32(this.offset, v >>> 0, false);
    this.offset += 4;
  }

  writeI32(v: number): void {
    this.ensure(4);
    this.view.setInt32(this.offset, v | 0, false);
    this.offset += 4;
  }

  /**
   * 64-bit unsigned big-endian write, value passed as a regular JS number.
   * Caller is responsible for staying within Number.MAX_SAFE_INTEGER —
   * everything PSB writes is well below that bound.
   */
  writeU64(v: number): void {
    if (v < 0 || !Number.isFinite(v)) {
      throw new BinaryWriterError(`writeU64: invalid value ${v}`);
    }
    if (v > Number.MAX_SAFE_INTEGER) {
      throw new BinaryWriterError(
        `writeU64: value ${v} exceeds Number.MAX_SAFE_INTEGER`,
      );
    }
    this.ensure(8);
    const hi = Math.floor(v / 0x1_0000_0000);
    const lo = v >>> 0;
    this.view.setUint32(this.offset, hi >>> 0, false);
    this.view.setUint32(this.offset + 4, lo, false);
    this.offset += 8;
  }

  writeDouble(v: number): void {
    this.ensure(8);
    this.view.setFloat64(this.offset, v, false);
    this.offset += 8;
  }

  // -- Bytes / strings -------------------------------------------------

  writeBytes(b: Uint8Array): void {
    if (b.byteLength === 0) return;
    this.ensure(b.byteLength);
    this.buf.set(b, this.offset);
    this.offset += b.byteLength;
  }

  /**
   * PSD-style Pascal string: 1-byte length, that many ASCII/MacRoman bytes,
   * then zero-padded so the *total* (length byte + content) is a multiple of
   * `pad`.  Mirrors `BinaryReader.readPascalString`.
   */
  writePascalString(s: string, pad: 1 | 2 | 4): void {
    if (s.length > 0xff) {
      throw new BinaryWriterError(
        `Pascal string too long: ${s.length} chars (max 255)`,
      );
    }
    this.writeU8(s.length);
    for (let i = 0; i < s.length; i++) {
      this.writeU8(s.charCodeAt(i) & 0xff);
    }
    const consumed = 1 + s.length;
    const over = consumed % pad;
    if (over !== 0) {
      const padLen = pad - over;
      for (let i = 0; i < padLen; i++) this.writeU8(0);
    }
  }

  /**
   * Photoshop "Unicode string": 4-byte length (character count) followed by
   * that many UTF-16 BE code units.  Mirrors `BinaryReader.readUnicodeString`.
   * No trailing NUL is added — callers that need it should include it in `s`.
   */
  writeUnicodeString(s: string): void {
    this.writeU32(s.length);
    this.ensure(s.length * 2);
    for (let i = 0; i < s.length; i++) {
      const code = s.charCodeAt(i) & 0xffff;
      this.view.setUint16(this.offset, code, false);
      this.offset += 2;
    }
  }

  /** Pad the cursor with zero bytes until it is a multiple of `toMultipleOf`. */
  pad(toMultipleOf: 1 | 2 | 4): void {
    const over = this.offset % toMultipleOf;
    if (over === 0) return;
    const need = toMultipleOf - over;
    this.ensure(need);
    // Buffer is zero-initialised by Uint8Array allocation; on a re-used buffer
    // the bytes may already be non-zero, so write explicitly.
    for (let i = 0; i < need; i++) {
      this.view.setUint8(this.offset + i, 0);
    }
    this.offset += need;
  }

  // -- Deferred length slots -------------------------------------------

  /**
   * Reserve a 4-byte big-endian length slot at the current offset.  Returns
   * a `commit` callback that back-patches the slot with the supplied length.
   * The caller is responsible for computing the length (typically as
   * `writer.length() - contentStart`).
   */
  reserveU32(): { commit: (totalContentBytes: number) => void } {
    this.ensure(4);
    const slot = this.offset;
    this.view.setUint32(slot, 0, false);
    this.offset += 4;
    return {
      commit: (totalContentBytes: number) => {
        if (totalContentBytes < 0 || !Number.isFinite(totalContentBytes)) {
          throw new BinaryWriterError(
            `reserveU32 commit: invalid length ${totalContentBytes}`,
          );
        }
        if (totalContentBytes > 0xffff_ffff) {
          throw new BinaryWriterError(
            `reserveU32 commit: length ${totalContentBytes} exceeds u32 range`,
          );
        }
        this.view.setUint32(slot, totalContentBytes >>> 0, false);
      },
    };
  }

  /**
   * Reserve an 8-byte big-endian length slot.  Used for PSB section lengths
   * and for the 13 spec-listed wide-length additional-info keys.
   */
  reserveU64(): { commit: (totalContentBytes: number) => void } {
    this.ensure(8);
    const slot = this.offset;
    this.view.setUint32(slot, 0, false);
    this.view.setUint32(slot + 4, 0, false);
    this.offset += 8;
    return {
      commit: (totalContentBytes: number) => {
        if (totalContentBytes < 0 || !Number.isFinite(totalContentBytes)) {
          throw new BinaryWriterError(
            `reserveU64 commit: invalid length ${totalContentBytes}`,
          );
        }
        if (totalContentBytes > Number.MAX_SAFE_INTEGER) {
          throw new BinaryWriterError(
            `reserveU64 commit: length ${totalContentBytes} exceeds safe integer range`,
          );
        }
        const hi = Math.floor(totalContentBytes / 0x1_0000_0000);
        const lo = totalContentBytes >>> 0;
        this.view.setUint32(slot, hi >>> 0, false);
        this.view.setUint32(slot + 4, lo, false);
      },
    };
  }
}
